// Notes editor — pure helper. _displayNoteTitle stays in index.html (shared with the parent
// session-summaries view) and is reached via the HUD bridge. autoGrow was retired in SC-N2
// (both textareas now fill their pane and scroll internally — no auto-grow).
const H = () => window.HUD || {};

// F-55c: a note's PLAIN text, one paragraph at a time, each dir="auto", so Hebrew and English lines align independently (as they do in the editor)
// instead of the whole text taking the direction of its first strong letter. A blank line keeps one line of height. Replaces the node's children.
export function paragraphsInto(node, text){
  const kids = String(text == null ? '' : text).split('\n').map(line => {
    const d = document.createElement('div'); d.setAttribute('dir', 'auto');
    if(line) d.textContent = line; else d.appendChild(document.createElement('br'));
    return d;
  });
  node.replaceChildren(...kids);
}

// Notes SC5 L2 (queue item 5) — the DEFAULT title stored for a new note: the app language's own date
// punctuation, 24-hour time (hourCycle h23 — never "24:xx", never a forced AM/PM), e.g. "26 Sept 2026, 14:30" /
// "26 בספט׳ 2026, 14:30". A note created from a session uses the session's date + start time. The stored
// title is only a fallback now: cards / header / parent view show the date computed from the data.
export function formatNoteTitle(date){
  const d = new Date(date); if(isNaN(d)) return String(date);
  return H()._locDate(d,{day:'numeric',month:'short',year:'numeric'})+', '+H()._locTime(d,{hour:'2-digit',minute:'2-digit',hourCycle:'h23'});
}

// True when a note's title is an auto default — any format the app ever stored, in any UI language:
//   • empty, or the legacy raw ISO date (== n.date);
//   • the legacy "<day month> · <year>, <h:mm>[ AM/PM]" shape (en-US / en-GB / he-IL months — _locDate/_locTime
//     follow the UI language, which is how the mixed "2 באוק׳ · 2026, 10:12 AM" titles came about);
//   • the SC5 shape "<date>, HH:MM" (formatNoteTitle above, any locale).
// A SHAPE test, so a title stays "default" when the UI language changes. A custom title never matches.
const _LEGACY_DEFAULT = /^.{2,30} ?· \d{4}(, \d{1,2}:\d{2}.{0,12})?$/; // (one stored title has no space before the ·; the oldest ones have no time)
const _SC5_DEFAULT = /^[^·\n]{3,30}\d{4}, \d{2}:\d{2}$/;
export function isDefaultNoteTitle(n){
  const t = (n && n.title || '').trim();
  if(!t) return true;
  if(t === n.date || /^\d{4}-\d{2}-\d{2}$/.test(t)) return true;
  return _LEGACY_DEFAULT.test(t) || _SC5_DEFAULT.test(t);
}
// The title to put in the editor's title field: a custom title, or '' for a default one (the field's
// placeholder then shows the computed date).
export function titleForInput(n){ return isDefaultNoteTitle(n) ? '' : (n.title || ''); }

// Notes SC5 L4 (decision 22): the patient the open note sheet is for. Over the calendar the sheet carries its own
// context (S._notesCtx) so the calendar's own scope (S.activeChild / S.activeSpecPatient) is never touched.
export function notePatientCtx(){ const S = H().S; return (S && S._notesCtx) || H()._currentPatientContext(); }

// ── Notes SC5 L2: the session a note belongs to ──
// The linked session (appointments embed from the specialist list fetch, else the in-memory appointment —
// realtime rows carry no embed). null → a general note: no link, or the session is deleted / hidden (RLS) /
// soft-deleted. The stored link is kept either way.
export function noteSession(n){
  if(!n || !n.appointmentId) return null;
  if(n.session) return n.session;
  const a = (H().DB && H().DB.appointments || []).find(x => x.id === n.appointmentId);
  if(!a || a.deletedAt) return null;
  return { date:a.date, time:a.time, title:a.title };
}
const _hm = (t) => { const m = String(t || '').match(/(\d{1,2}):(\d{2})/); return m ? m[1].padStart(2,'0')+':'+m[2] : ''; };
// Session date for a linked note (its appointment's date + start time), else the note's written date.
// An ISO-ish sort key: newest first = descending.
export function noteSortKey(n){
  const s = noteSession(n);
  if(s) return (s.date||'') + 'T' + (_hm(s.time)||'00:00');
  if(n && n.writtenAt){ const d = new Date(n.writtenAt); if(!isNaN(d)) return H()._ymd(d)+'T'+String(d.getHours()).padStart(2,'0')+':'+String(d.getMinutes()).padStart(2,'0'); }
  return (n && n.date || '') + 'T00:00';
}
// The date shown for a note (cards, header, parent view), computed from the data — never the stored title.
// Linked: the session's date + 24-hour start time; general: the written date. Callers wrap it in <bdi dir=auto>.
export function noteDateLabel(n, opts={}){
  const s = noteSession(n);
  const fmtDate = (ymd) => { const d = new Date(String(ymd)+'T12:00:00'); return isNaN(d) ? (ymd||'') : H()._locDate(d,{day:'numeric',month:'short',year:'numeric'}); };
  if(s){ const t = _hm(s.time); return fmtDate(s.date) + (t && opts.time!==false ? ' · '+t : ''); }
  if(n && n.writtenAt){ const d = new Date(n.writtenAt); if(!isNaN(d)) return H()._locDate(d,{day:'numeric',month:'short',year:'numeric'}); }
  return fmtDate(n && n.date);
}
// The session type shown next to the date (the appointment's title — what the calendar shows for specialists).
export function noteSessionType(n){ const s = noteSession(n); return s ? (s.title || '') : ''; }

// Notes SC2 — shared LOCAL "HH:MM" for a saved time (the autosave pill + the header chip). An ISO timestamp
// (DB last_saved) is converted to local time; the old regex read the UTC hour straight out of the ISO string.
// A legacy local "YYYY-MM-DD HH:MM" string (pre-SC2 Save) still reads its own HH:MM.
export function noteTimeHM(v){
  if(!v) return '';
  try{
    const s=String(v);
    if(/^\d{4}-\d{2}-\d{2}T/.test(s)){ const d=new Date(s); if(!isNaN(d)) return H()._locTime(d,{hour:'2-digit',minute:'2-digit'}); }
    const m=s.match(/(\d{1,2}:\d{2})/); if(m) return m[1];
    const d=new Date(v); if(!isNaN(d)) return H()._locTime(d,{hour:'2-digit',minute:'2-digit'});
  }catch(e){}
  return '';
}
const _shortTime = noteTimeHM;

// Shared state-chip computation for the header chip, the list rows, and the past-session rows/window.
// opts.isOpenNote gates the live "Unsaved" state (only the note being edited); opts.withTime adds the
// "· HH:MM" to Saved (header only — the list/rail show "Saved" alone).
export function computeNoteChip(n, opts={}){
  const { isOpenNote=false, withTime=true } = opts;
  const T = H().T, S = H().S;
  if(n.locked) return { label:T('vn2_state_locked'), bg:'#fef3c7', fg:'#92400e' };
  if(n.published) return { label:T('vn2_state_published'), bg:'#dcfce7', fg:'#166534' };
  if(n.pendingPublish) return { label:T('vn2_state_pending'), bg:'#fef3c7', fg:'#92400e' };
  if(isOpenNote && S.vaultHasUnsaved) return { label:T('vn2_state_unsaved'), bg:'#fef3c7', fg:'#92400e' };
  if(n.isDraft) return { label:T('vn2_state_draft'), bg:'var(--mint-l)', fg:'var(--slate)' };
  const _t=withTime?_shortTime(n.lastSaved):''; return { label:T('vn2_state_saved')+(_t?(' · '+_t):''), bg:'#dcfce7', fg:'#166534' };
}
